import { JsonObject, JsonProperty } from 'json2typescript';

import { AnnualNetIncome, ContractualTime, EmploymentSituation, ActivityWithoutIncomeSource, ProfessionalInterface, SourceFunds, SourceIncome } from '../../../core';
import { AnnualNetIncomeAdapter } from '../../client/helpers/serializer/adapters/annual-net-income.adapter';
import { ContractualTimeAdapter } from '../../client/helpers/serializer/adapters/contractual-time.adapter';
import { EmploymentSituationAdapter } from '../../client/helpers/serializer/adapters/employment-situation.adapter';
import { ActivityWithoutIncomeSourceAdapter } from '../../client/helpers/serializer/adapters/activity-without-income-source.adapter';
import { SourceFundsAdapter } from '../../client/helpers/serializer/adapters/source-funds.adapter';
import { SourceIncomeAdapter } from '../../client/helpers/serializer/adapters/source-income.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { StringToNumberAdapter } from '../../client/helpers/serializer/adapters/string-to-number.adapter';

@JsonObject('Professional')
export class Professional implements ProfessionalInterface {
    @JsonProperty('EmploymentSituation', EmploymentSituationAdapter, true)
    private _employmentSituation: EmploymentSituation = undefined;

    @JsonProperty('ActivityWithoutIncomeSource', ActivityWithoutIncomeSourceAdapter, true)
    private _activityWithoutIncomeSource: ActivityWithoutIncomeSource = undefined;

    @JsonProperty('CompanyName', StringAdapter, true)
    private _companyName: string = undefined;

    @JsonProperty('CompanyActivity', StringAdapter, true)
    private _companyActivity: string = undefined;

    @JsonProperty('Position', StringAdapter, true)
    private _position: string = undefined;

    @JsonProperty('ContractualStartDate', ContractualTimeAdapter, true)
    private _contractualTime: ContractualTime = undefined;

    @JsonProperty('IsMultiplesActivities', Boolean, true)
    private _isMultiplesActivities: boolean = undefined;

    @JsonProperty('AdditionalActivitiesInfo', StringAdapter, true)
    private _additionalActivitiesInfo: string = undefined;

    @JsonProperty('SourceOfIncome', SourceIncomeAdapter, true)
    private _sourceOfIncome: SourceIncome[] = undefined;

    @JsonProperty('SourceOfIncomeFreeText', StringAdapter, true)
    private _sourceOfIncomeFreeText: string = undefined;

    @JsonProperty('SourceOfFunds', SourceFundsAdapter, true)
    private _sourceOfFunds: SourceFunds = undefined;

    @JsonProperty('SourceOfFundsFreeText', StringAdapter, true)
    private _sourceOfFundsFreeText: string = undefined;

    @JsonProperty('AnnualNetIncome', AnnualNetIncomeAdapter, true)
    private _annualNetIncome: AnnualNetIncome = undefined;

    @JsonProperty('PurposeKYC', StringToNumberAdapter, true)
    private _purposeKYC: number = undefined;

    @JsonProperty('SourceOfFundsInversis', SourceFundsAdapter, true)
    private _sourceOfFundsKYC: SourceFunds = undefined;

    @JsonProperty('NumberOfTransfersKYC', StringToNumberAdapter, true)
    private _numberOfTransfersKYC: number = undefined;

    @JsonProperty('NumberOfTransactionsKYC', StringToNumberAdapter, true)
    private _numberOfTransactionsKYC: number = undefined;

    @JsonProperty('EstimatedPositionKYC', StringToNumberAdapter, true)
    private _estimatedPositionKYC: number = undefined;

    @JsonProperty('HasRiskActivity', Boolean, true)
    private _hasRiskActivity: boolean = undefined;

    @JsonProperty('RiskActivityCode', StringAdapter, true)
    private _riskActivityCode: string = undefined;

    @JsonProperty('ActivityWithoutIncome', StringAdapter, true)
    private _activityWithoutIncome: string = undefined;

    @JsonProperty('ActivityWithoutIncomeOthers', StringAdapter, true)
    private _activityWithoutIncomeOthers: string = undefined;
    private _doAnyActivity: boolean = undefined;

    constructor() {
        this._doAnyActivity = true;
    }

    get employmentSituation(): EmploymentSituation {
        return this._employmentSituation;
    }

    set employmentSituation(value: EmploymentSituation) {
        this._employmentSituation = value;
        this._activityWithoutIncome = undefined;
        if ([EmploymentSituation.RECIPIENT_SUBSIDIES, EmploymentSituation.UNPAID_ACTIVITY].includes(value)) {
            this.companyName = undefined;
            this.companyActivity = undefined;
            this.contractualTime = undefined;
        }
    }

    get activityWithoutIncomeSource(): ActivityWithoutIncomeSource {
        return this._activityWithoutIncomeSource;
    }

    set activityWithoutIncomeSource(value: ActivityWithoutIncomeSource) {
        this._activityWithoutIncomeSource = value;
    }

    get companyName(): string {
        return this._companyName;
    }

    set companyName(value: string) {
        this._companyName = value;
    }

    get companyActivity(): string {
        return this._companyActivity;
    }

    set companyActivity(value: string) {
        this._companyActivity = value;
    }

    get position(): string {
        return this._position;
    }

    set position(value: string) {
        this._position = value;
    }

    get contractualTime(): ContractualTime {
        return this._contractualTime;
    }

    set contractualTime(value: ContractualTime) {
        this._contractualTime = value;
    }

    get isMultiplesActivities(): boolean {
        return this._isMultiplesActivities;
    }

    set isMultiplesActivities(value: boolean) {
        this._isMultiplesActivities = value;
    }

    get additionalActivitiesInfo(): string {
        return this._additionalActivitiesInfo;
    }

    set additionalActivitiesInfo(value: string) {
        this._additionalActivitiesInfo = value;
    }

    get sourceOfIncome(): SourceIncome {
        if (!this._sourceOfIncome || this._sourceOfIncome.length === 0) {
            return undefined;
        }

        return this._sourceOfIncome[0];
    }

    set sourceOfIncome(value: SourceIncome) {
        if (!this._sourceOfIncome) {
            this._sourceOfIncome = [];
        }

        this._sourceOfIncome[0] = value;
    }

    get sourceOfIncomeFreeText(): string {
        return this._sourceOfIncomeFreeText;
    }

    set sourceOfIncomeFreeText(value: string) {
        this._sourceOfIncomeFreeText = value;
    }

    get sourceOfFunds(): SourceFunds {
        return this._sourceOfFunds;
    }

    set sourceOfFunds(value: SourceFunds) {
        this._sourceOfFunds = value;
    }

    get sourceOfFundsFreeText(): string {
        return this._sourceOfFundsFreeText;
    }

    set sourceOfFundsFreeText(value: string) {
        this._sourceOfFundsFreeText = value;
    }

    get annualNetIncome(): AnnualNetIncome {
        return this._annualNetIncome;
    }

    set annualNetIncome(value: AnnualNetIncome) {
        this._annualNetIncome = value;
    }

    get purposeKYC(): number {
        return this._purposeKYC > 0 ? this._purposeKYC : undefined;
    }

    set purposeKYC(value: number) {
        this._purposeKYC = value;
    }

    get sourceOfFundsKYC(): SourceFunds {
        return this._sourceOfFundsKYC;
    }

    set sourceOfFundsKYC(value: SourceFunds) {
        this._sourceOfFundsKYC = value;
    }

    get numberOfTransfersKYC(): number {
        return this._numberOfTransfersKYC > 0 ? this._numberOfTransfersKYC : undefined;
    }

    set numberOfTransfersKYC(value: number) {
        this._numberOfTransfersKYC = value;
    }

    get numberOfTransactionsKYC(): number {
        return this._numberOfTransactionsKYC > 0 ? this._numberOfTransactionsKYC : undefined;
    }

    set numberOfTransactionsKYC(value: number) {
        this._numberOfTransactionsKYC = value;
    }

    get estimatedPositionKYC(): number {
        return this._estimatedPositionKYC > 0 ? this._estimatedPositionKYC : undefined;
    }

    set estimatedPositionKYC(value: number) {
        this._estimatedPositionKYC = value;
    }

    get doAnyActivity(): boolean {
        return this._doAnyActivity;
    }

    set doAnyActivity(value: boolean) {
        this._doAnyActivity = value;
        if (!value) {
            this.employmentSituation = undefined;
            this.activityWithoutIncomeSource = undefined;
            this.position = undefined;
            this.contractualTime = undefined;
            this.isMultiplesActivities = undefined;
            this.additionalActivitiesInfo = undefined;
            this.sourceOfFunds = undefined;
            this.annualNetIncome = undefined;
        }
    }

    get hasRiskActivity(): boolean {
        return this._hasRiskActivity;
    }

    set hasRiskActivity(value: boolean) {
        this._hasRiskActivity = value;
    }

    get riskActivityCode(): string {
        return this._riskActivityCode;
    }

    set riskActivityCode(value: string) {
        this._riskActivityCode = value;
    }

    get activityWithoutIncome(): string {
        return this._activityWithoutIncome;
    }

    set activityWithoutIncome(value: string) {
        this._activityWithoutIncome = value;
        this._employmentSituation = undefined;
    }

    get activityWithoutIncomeOthers(): string {
        return this._activityWithoutIncomeOthers;
    }

    set activityWithoutIncomeOthers(value: string) {
        this._activityWithoutIncomeOthers = value;
    }

    isComplete(): boolean {
        if (this.doAnyActivity) {
            if (!!this.activityWithoutIncome == true) {

                return (
                    !!this.activityWithoutIncome && this.isMultiplesActivities !== undefined &&
                    (this.isMultiplesActivities ? !!this.additionalActivitiesInfo : true) &&
                    ([SourceFunds.OTHER].indexOf(this.sourceOfFunds) !== -1 ? !!this.sourceOfFundsFreeText : !!this.sourceOfFunds) &&
                    (!!this.annualNetIncome && this.activityWithoutIncome != '6' ? true : !!this.activityWithoutIncomeOthers) && !!this.position
                );
            } else {
                return (
                    !!this.employmentSituation &&
                    ([EmploymentSituation.AS_EMPLOYEE, EmploymentSituation.SELF_EMPLOYED].indexOf(this.employmentSituation) !== -1
                        ? !!this.companyName && !!this.companyActivity && !!this.contractualTime && !!this.position
                        : true) &&
                        ([EmploymentSituation.AS_EMPLOYEE, EmploymentSituation.SELF_EMPLOYED].indexOf(this.employmentSituation) === -1
                        ? !!this.sourceOfIncome && !!this.position : true) &&
                     this.isMultiplesActivities !== undefined &&
                    (!!this.isMultiplesActivities ? !!this.additionalActivitiesInfo : true) &&

                    ([SourceFunds.OTHER].indexOf(this.sourceOfFunds) !== -1 ? !!this.sourceOfFundsFreeText : !!this.sourceOfFunds) &&
                    !!this.annualNetIncome &&
                    (this.sourceOfIncome !=  'TR' ? true : !!this.sourceOfIncomeFreeText)
                );
            }
        }
        return !!this.position && !!this.sourceOfIncome;
    }

    isCompleteInversis(): boolean {
        return (
            !!this.purposeKYC &&
            !!this.sourceOfFundsKYC &&
            !!this.numberOfTransfersKYC &&
            !!this.numberOfTransactionsKYC &&
            !!this.estimatedPositionKYC
        );
    }
}
